import { inject } from 'vue';

import { cloneDeep } from 'lodash';

import { defineStore } from 'pinia';
import { CreateDocumentCategoryCommand } from 'src/stores/document/commands/CreateDocumentCategoryCommand';
import { CreateDocumentCommand } from 'src/stores/document/commands/CreateDocumentCommand';
import { CreateDocumentVersionCommand } from 'src/stores/document/commands/CreateDocumentVersionCommand';
import { DocumentDeleteByAggregateIdCommand } from 'src/stores/document/commands/DocumentDeleteByAggregateIdCommand';
import { DocumentDeleteCommand } from 'src/stores/document/commands/DocumentDeleteCommand';
import { ProcessDocumentCommand } from 'src/stores/document/commands/ProcessDocumentCommand';
import { GetTheDetailsForTheDocumentQuery, GetThePreviewForTheDocumentQuery } from 'src/stores/document/queries';

import { CommandProcessIdHandling } from 'src/models/command';
import {
	Document,
	DocumentDownloadLink,
	DocumentPreview,
	DocumentPreviewRunType,
	DocumentRunType,
} from 'src/models/document';
import { SetTheStateForMultipleAggregates } from 'src/models/status/SetStatusCommand';
import { StatusEnum } from 'src/models/status/enums';

import { AggregateComparator } from 'src/util/aggregate/AggregateComparator';
import { AggregateMerger } from 'src/util/aggregate/AggregateMerger';
import {
	ApplyTheStatusForTheAggregateIdsPayload,
	aggregateEntryId,
	aggregateIdWithUnsetSourceAndTargetContainerId,
	applyTheStatusForTheAggregateIdsInRecordMaps,
} from 'src/util/aggregate/AggregateUtilityFunctions';
import { CommandBus } from 'src/util/command/CommandBus';
import { selectedCategoryIdForIdoGroup, withSelectedCategory } from 'src/util/document/documentCategorySelection';

import { DocumentDetailsCallback } from './callbacks/DocumentDetailsCallback';
import { DocumentPreviewCallback } from './callbacks/DocumentPreviewCallback';
import {
	CreateTheDocumentCategoryPayload,
	CreateTheDocumentPayload,
	CreateTheDocumentVersionPayload,
	DeleteTheDocumentByAggregateIdPayload,
	DeleteTheDocumentPayload,
	DocumentStoreState,
	FetchDocumentPayload,
	FetchDocumentPreviewPayload,
	ResetPayload,
	SaveTheDocumentPayload,
	SetDocumentPreviewPayload,
	SetTheDocumentStatePayload,
	UnsubscribeFetchDocumentPayload,
	UpdateTheDocumentBoxIdPayload,
	UpdateTheDocumentBoxPayload,
	UpdateTheDocumentBuzzwordsPayload,
	UpdateTheDocumentCategorySelectionPayload,
	UpdateTheDocumentFilenamePayload,
	UpdateTheDocumentTitlePayload,
} from './interfaces';

const normalizeAggregateId = (aggregateId: string): string =>
	aggregateIdWithUnsetSourceAndTargetContainerId(aggregateId);

const createDefaultState = (): DocumentStoreState => ({
	documentPreviews: new Map<string, DocumentPreview | string>(),
	documents: new Map<string, Document>(),
	initialLoadedDocuments: new Map<string, Document>(),
	newDocumentAggregateId: '',
});

const resolveStateCommandPayload = (
	payload: SetTheDocumentStatePayload | string
): { aggregateId: string; commandBus: CommandBus | undefined } => {
	if (typeof payload === 'string') {
		return {
			aggregateId: payload,
			commandBus: inject<CommandBus>('commandBus'),
		};
	}

	return {
		aggregateId: payload.aggregateId,
		commandBus: payload.commandBus ?? inject<CommandBus>('commandBus'),
	};
};

const parseCreatedDocumentAggregateId = (authenticatedUserId: number, result: unknown): string => {
	const documentId = (
		result as {
			data?: {
				data?: {
					documentId?: number;
				};
			};
		} | null
	)?.data?.data?.documentId;

	if (typeof documentId !== 'number') {
		return '';
	}

	return `{"moduleName":"module_document","entryId":${documentId},"userId":${authenticatedUserId},"sourceContainerId":0,"targetContainerId":0}`;
};

export const useDocumentStore = defineStore('document', {
	actions: {
		applyTheStatusForTheAggregateIds(payload: ApplyTheStatusForTheAggregateIdsPayload): void {
			applyTheStatusForTheAggregateIdsInRecordMaps(
				[this.documents, this.initialLoadedDocuments],
				payload.aggregateIds,
				payload.status
			);
		},
		async createTheDocument(payload: CreateTheDocumentPayload): Promise<boolean> {
			const result = await payload.commandBus?.sendCommand(
				new CreateDocumentCommand(
					payload.documentFileName,
					payload.documentFileSize,
					payload.documentFileType,
					payload.documentState,
					payload.documentBoxId,
					payload.documentKeyWords,
					payload.documentFileTitle,
					payload.documentFileData,
					payload.idoLinkings
				),
				{ onUploadProgress: payload.onUploadProgress }
			);

			const createdAggregateId = parseCreatedDocumentAggregateId(payload.authenticatedUserId, result);
			this.setNewDocumentAggregateId(createdAggregateId);

			return createdAggregateId !== '';
		},
		async createTheDocumentCategory(payload: CreateTheDocumentCategoryPayload): Promise<void> {
			const documentId = aggregateEntryId(payload.aggregateId);

			if (documentId === null || documentId === 0) {
				return;
			}

			await payload.commandBus?.sendCommand(
				new CreateDocumentCategoryCommand({
					documentId,
					idoId: payload.idoId,
					idoInstanceId: payload.idoInstanceId,
					isGlobal: payload.isGlobal,
					name: payload.name,
				})
			);

			if (payload.queryStore === undefined) {
				return;
			}

			await this.fetchDocument({
				aggregateId: payload.aggregateId,
				queryStore: payload.queryStore,
			});

			const refetchedDocument = this.initialLoadedDocument(payload.aggregateId);
			const affectedGroup = refetchedDocument?.desktopPanelDocumentModelIdoCategories.find(
				(group) => group.idoId === payload.idoId && group.idoInstanceId === payload.idoInstanceId
			);

			if (affectedGroup === undefined) {
				return;
			}

			const serverSelectedCategoryId = selectedCategoryIdForIdoGroup(affectedGroup);

			if (serverSelectedCategoryId === null) {
				return;
			}

			this.updateTheDocumentCategorySelection({
				aggregateId: payload.aggregateId,
				categoryId: serverSelectedCategoryId,
				idoId: payload.idoId,
				idoInstanceId: payload.idoInstanceId,
			});
		},
		async createTheDocumentVersion(payload: CreateTheDocumentVersionPayload): Promise<boolean> {
			const result = await payload.commandBus?.sendCommand(
				new CreateDocumentVersionCommand(
					payload.aggregateId,
					payload.aggregateVersion,
					payload.documentFileName,
					payload.documentFileType,
					payload.documentFileSize,
					payload.documentFileData
				)
			);

			return result != null;
		},
		async deleteTheDocument(payload: DeleteTheDocumentPayload): Promise<boolean> {
			const documentFromCollection = this.documents.get(normalizeAggregateId(payload.aggregateId));

			if (documentFromCollection === undefined) {
				return false;
			}

			const response = await payload.commandBus?.sendCommand(new DocumentDeleteCommand(documentFromCollection));

			if (response === null || response === undefined) {
				return false;
			}

			this.reset({
				aggregateId: payload.aggregateId,
			});

			return true;
		},
		deleteTheDocumentByAggregateId(payload: DeleteTheDocumentByAggregateIdPayload): void {
			void payload.commandBus
				?.sendCommand(
					new DocumentDeleteByAggregateIdCommand(
						payload.aggregateId,
						payload.aggregateVersion,
						payload.processId
					)
				)
				?.then(() => {
					this.reset({
						aggregateId: payload.aggregateId,
					});
				});
		},
		document(aggregateId: string): Document | undefined {
			return this.documents.get(aggregateId);
		},
		documentPreview(aggregateId: string): DocumentPreview | string | undefined {
			return this.documentPreviews.get(aggregateId);
		},
		fetchDocument(payload: FetchDocumentPayload): Promise<void> {
			if (payload.queryStore !== undefined) {
				return payload.queryStore.query<Document>({
					callback: new DocumentDetailsCallback((document) => this.setDocument(document)),
					createTime: new Date(),
					query: new GetTheDetailsForTheDocumentQuery(payload.aggregateId),
					responseType: 'json',
					runType: DocumentRunType,
				});
			}

			this.reset({
				aggregateId: payload.aggregateId,
			});
			return Promise.resolve();
		},
		fetchDocumentPreview(payload: FetchDocumentPreviewPayload): void {
			if (payload.queryStore !== undefined) {
				void payload.queryStore.query<DocumentPreview>({
					callback: new DocumentPreviewCallback(payload.aggregateId, (previewPayload) =>
						this.setDocumentPreview(previewPayload)
					),
					createTime: new Date(),
					query: new GetThePreviewForTheDocumentQuery(payload.aggregateId, payload.version),
					responseType: 'blob',
					runType: DocumentPreviewRunType,
				});
				return;
			}

			this.setDocumentPreview({
				aggregateId: payload.aggregateId,
				payload: null,
			});
		},
		initialLoadedDocument(aggregateId: string): Document | undefined {
			return this.initialLoadedDocuments.get(normalizeAggregateId(aggregateId));
		},
		markDocumentAsSaved(aggregateId: string): void {
			const normalizedAggregateId = normalizeAggregateId(aggregateId);
			const documentFromCollection = this.documents.get(normalizedAggregateId);
			if (documentFromCollection !== undefined) {
				this.initialLoadedDocuments.set(normalizedAggregateId, cloneDeep<Document>(documentFromCollection));
			}
		},
		reset(payload: ResetPayload): void {
			const normalizedAggregateId = normalizeAggregateId(payload.aggregateId);
			this.documents.delete(normalizedAggregateId);
			this.initialLoadedDocuments.delete(normalizedAggregateId);
			this.documentPreviews.delete(normalizedAggregateId);
		},
		async saveTheDocument(payload: SaveTheDocumentPayload): Promise<boolean> {
			const documentFromCollection = this.documents.get(normalizeAggregateId(payload.aggregateId));

			if (documentFromCollection === undefined) {
				return false;
			}

			const response = await payload.commandBus?.sendCommand(new ProcessDocumentCommand(documentFromCollection));

			return response !== null && response !== undefined;
		},
		setDocument(payload: Document | null | undefined): void {
			if (payload == null || typeof payload.aggregateId !== 'string' || payload.aggregateId === '') {
				return;
			}

			const normalizedAggregateId = normalizeAggregateId(payload.aggregateId);
			const documentFromCollection = this.documents.get(normalizedAggregateId);
			const initialLoadedDocumentFromCollection = this.initialLoadedDocuments.get(normalizedAggregateId);

			if (documentFromCollection !== undefined && !AggregateComparator.isEqual(documentFromCollection, payload)) {
				return;
			}

			if (documentFromCollection !== undefined && initialLoadedDocumentFromCollection !== undefined) {
				const mergedDocument = AggregateMerger.merge(
					{ ...initialLoadedDocumentFromCollection, aggregateId: payload.aggregateId },
					{ ...documentFromCollection, aggregateId: payload.aggregateId },
					payload
				) as Document;
				this.documents.set(normalizeAggregateId(mergedDocument.aggregateId), cloneDeep(mergedDocument));
			} else {
				this.documents.set(normalizedAggregateId, payload);
			}

			this.initialLoadedDocuments.set(normalizedAggregateId, cloneDeep(payload));
		},
		setDocumentPreview(payload: SetDocumentPreviewPayload): void {
			this.documentPreviews.set(normalizeAggregateId(payload.aggregateId), payload.payload || '');
		},
		setNewDocumentAggregateId(payload: string): void {
			this.newDocumentAggregateId = payload || '';
		},
		setTheDocumentDownloadLink(payload: {
			aggregateId: string;
			downloadLink: DocumentDownloadLink | undefined;
		}): void {
			const normalizedAggregateId = normalizeAggregateId(payload.aggregateId);
			const documentFromCollection = this.documents.get(normalizedAggregateId);
			const initialLoadedDocumentFromCollection = this.initialLoadedDocuments.get(normalizedAggregateId);

			if (documentFromCollection !== undefined) {
				this.documents.set(normalizedAggregateId, {
					...documentFromCollection,
					downloadLink: payload.downloadLink,
				});
			}

			if (initialLoadedDocumentFromCollection !== undefined) {
				this.initialLoadedDocuments.set(normalizedAggregateId, {
					...initialLoadedDocumentFromCollection,
					downloadLink: payload.downloadLink,
				});
			}
		},
		setTheDocumentStateToReady(payload: SetTheDocumentStatePayload | string): void {
			const resolvedPayload = resolveStateCommandPayload(payload);
			void resolvedPayload.commandBus?.sendCommand(
				new SetTheStateForMultipleAggregates(
					[resolvedPayload.aggregateId],
					CommandProcessIdHandling.IGNORING,
					StatusEnum.READY
				)
			);
		},
		setTheDocumentStateToSeen(payload: SetTheDocumentStatePayload | string): void {
			const resolvedPayload = resolveStateCommandPayload(payload);
			void resolvedPayload.commandBus?.sendCommand(
				new SetTheStateForMultipleAggregates(
					[resolvedPayload.aggregateId],
					CommandProcessIdHandling.IGNORING,
					StatusEnum.SEEN
				)
			);
		},
		setTheDocumentStateToUnseen(payload: SetTheDocumentStatePayload | string): void {
			const resolvedPayload = resolveStateCommandPayload(payload);
			void resolvedPayload.commandBus?.sendCommand(
				new SetTheStateForMultipleAggregates(
					[resolvedPayload.aggregateId],
					CommandProcessIdHandling.IGNORING,
					StatusEnum.UNSEEN
				)
			);
		},
		async unsubscribeFetchDocument(payload: UnsubscribeFetchDocumentPayload): Promise<void> {
			if (payload.queryStore !== undefined) {
				await payload.queryStore.unsubscribeQuery(new GetTheDetailsForTheDocumentQuery(payload.aggregateId));
			}

			this.reset({
				aggregateId: payload.aggregateId,
			});
		},
		updateTheDocumentBox(payload: UpdateTheDocumentBoxPayload): void {
			const documentFromCollection = this.documents.get(normalizeAggregateId(payload.aggregateId));
			if (documentFromCollection !== undefined) {
				documentFromCollection.postbox = payload.payload || '';
				this.documents.set(normalizeAggregateId(documentFromCollection.aggregateId), documentFromCollection);
			}
		},
		updateTheDocumentBoxId(payload: UpdateTheDocumentBoxIdPayload): void {
			const documentFromCollection = this.documents.get(normalizeAggregateId(payload.aggregateId));
			if (documentFromCollection !== undefined) {
				documentFromCollection.postboxId = payload.payload || 0;
				this.documents.set(normalizeAggregateId(documentFromCollection.aggregateId), documentFromCollection);
			}
		},
		updateTheDocumentBuzzwords(payload: UpdateTheDocumentBuzzwordsPayload): void {
			const documentFromCollection = this.documents.get(normalizeAggregateId(payload.aggregateId));
			if (documentFromCollection !== undefined) {
				documentFromCollection.buzzwords = payload.payload || '';
				this.documents.set(normalizeAggregateId(documentFromCollection.aggregateId), documentFromCollection);
			}
		},
		updateTheDocumentCategorySelection(payload: UpdateTheDocumentCategorySelectionPayload): void {
			const documentFromCollection = this.documents.get(normalizeAggregateId(payload.aggregateId));
			if (documentFromCollection !== undefined) {
				documentFromCollection.desktopPanelDocumentModelIdoCategories = withSelectedCategory(
					documentFromCollection.desktopPanelDocumentModelIdoCategories,
					payload.idoId,
					payload.idoInstanceId,
					payload.categoryId
				);
				this.documents.set(normalizeAggregateId(documentFromCollection.aggregateId), documentFromCollection);
			}
		},
		updateTheDocumentFilename(payload: UpdateTheDocumentFilenamePayload): void {
			const documentFromCollection = this.documents.get(normalizeAggregateId(payload.aggregateId));
			if (documentFromCollection !== undefined) {
				documentFromCollection.filename = payload.payload || '';
				this.documents.set(normalizeAggregateId(documentFromCollection.aggregateId), documentFromCollection);
			}
		},
		updateTheDocumentTitle(payload: UpdateTheDocumentTitlePayload): void {
			const documentFromCollection = this.documents.get(normalizeAggregateId(payload.aggregateId));
			if (documentFromCollection !== undefined) {
				documentFromCollection.title = payload.payload || '';
				this.documents.set(normalizeAggregateId(documentFromCollection.aggregateId), documentFromCollection);
			}
		},
	},
	state: () => createDefaultState(),
});
export * from './interfaces';
export * from './types';
